import { Platform, StyleProp, View, ViewStyle } from 'react-native'
import cn from '@ui/utils/cn'
import TileGlass from '@ui/tiles/TileGlass'
import Typography from '@ui/typography'
import { TileBlurBadge, TileBlurProps, TileBlurVariant } from './types'
import { descVariantMap, padding, titleVariantMap, sInner } from './styles'
import useRWD from '@ui/theme/useRWD'
import { TypographyProps } from '@ui/typography/types'
import { TileGlassProps } from '@ui/tiles/TileGlass/types'
import { createContext, useContext, useMemo } from 'react'
import Status from '@ui/elements/Status'

const TileBlurContext = createContext<{ variant: TileBlurVariant }>({
  variant: 'default',
})

function TileBlurProvider({
  children,
  variant,
}: {
  children: React.ReactNode
  variant: TileBlurVariant
}) {
  const value = useMemo(() => ({ variant }), [variant])
  return (
    <TileBlurContext.Provider value={value}>
      {children}
    </TileBlurContext.Provider>
  )
}

const TileBlurTitle = ({
  title,
  desc,
  body,
  badge,
  titleVariant,
  className,
  classNameTitle,
  classNameDesc,
}: {
  title: string
  desc?: string | React.ReactNode
  body?: React.ReactNode
  badge?: TileBlurBadge
  titleVariant?: TypographyProps['variant']
  className?: string
  classNameTitle?: string
  classNameDesc?: string
}) => {
  const { variant } = useContext(TileBlurContext)
  return (
    <View className={cn(sInner[variant], className)}>
      <View
        className={cn(
          'flex w-full flex-row items-center justify-start gap-4',
          variant === 'option' ? 'w-2/3' : 'w-full'
        )}
      >
        <Typography
          variant={titleVariant || titleVariantMap[variant]}
          className={cn(classNameTitle)}
        >
          {title}
        </Typography>
        {badge && <Status text={badge.text} variant={badge.variant} />}
      </View>
      {body}
      {desc && (
        <Typography
          variant={descVariantMap[variant]}
          className={cn(
            'color-desc',
            variant !== 'small' &&
              Platform.select({
                default: 'w-2/3 pr-8',
                web: 'w-2/3 pr-20',
              }),
            variant === 'small' &&
              Platform.select({
                default: 'line-clamp-5 w-3/4 pr-0',
                web: 'line-clamp-5 w-2/3 pr-0',
              }),
            classNameDesc
          )}
        >
          {desc}
        </Typography>
      )}
    </View>
  )
}

function TileBlurGlass({
  style,
  children,
  ...glassProps
}: {
  children?: React.ReactNode
  style?: StyleProp<ViewStyle>
} & Partial<TileGlassProps>) {
  const { tiles } = useRWD()
  const { variant } = useContext(TileBlurContext)

  const height: Record<TileBlurVariant, number> = {
    large: tiles.blur.height,
    default: tiles.blur.height,
    option: tiles.blurOption.height,
    small: tiles.blur.height,
  }

  return (
    <TileGlass {...glassProps} style={[{ height: height[variant] }, style]}>
      {children}
    </TileGlass>
  )
}

function TileBlurContainer({
  className,
  children,
}: {
  children?: React.ReactNode
  className?: string
}) {
  const { variant } = useContext(TileBlurContext)
  return (
    <View
      className={cn(
        'relative h-full justify-between',
        padding[variant],
        className
      )}
    >
      {children}
    </View>
  )
}

/**
 * TileBlur component
 * Pre-composed with Glass, Title and Container
 */
function TileBlur({
  title,
  desc,
  body,
  cta,
  glassProps,
  badge,
  variant = 'default',
  style = {},
  classNameOuter,
  classNameInner,
  classNameTitle,
  classNameDesc,
  titleVariant,
  children,
}: TileBlurProps) {
  return (
    <TileBlurProvider variant={variant}>
      <TileBlurGlass {...glassProps} style={style}>
        <TileBlurContainer className={classNameOuter}>
          <TileBlurTitle
            title={title}
            desc={desc}
            body={body}
            badge={badge}
            titleVariant={titleVariant}
            classNameTitle={classNameTitle}
            classNameDesc={classNameDesc}
            className={classNameInner}
          />
          <View className="flex-row justify-start">
            {cta}
            {children}
          </View>
        </TileBlurContainer>
      </TileBlurGlass>
    </TileBlurProvider>
  )
}

TileBlur.Provider = TileBlurProvider
TileBlur.Title = TileBlurTitle
TileBlur.Container = TileBlurContainer
TileBlur.Glass = TileBlurGlass

export default TileBlur
